/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * The visitor's chosen language, shared with the support forum.
 *
 * The site keeps the language in localStorage, which a server cannot read.
 * The support forum draws its signed-out pages on the server, in the language
 * of a `dsk_lang` cookie, and writes that same cookie when a visitor picks a
 * language there. Writing it here too means a visitor who chose German on the
 * website opens the forum in German on the first page.
 *
 * The cookie, exactly as the forum reads and writes it: name `dsk_lang`; value
 * one of en, de, fr, es, pt, it; Path=/; Max-Age one year; SameSite=Lax;
 * Secure on HTTPS; Domain=deskshare.com on a deskshare.com host (so every
 * deskshare.com host shares it) and no Domain anywhere else; readable by page
 * scripts, because the forum's bar reads it.
 *
 * What must still not happen, whatever else changes here:
 *   - writing it when nobody picked a language (a page load in English must
 *     not overwrite a choice made on the forum) - only the three language
 *     controls call this;
 *   - writing any value but the six codes;
 *   - a Domain on a host that is not deskshare.com (the browser would refuse
 *     the cookie, or a look-alike host would share it).
 */

/** The six codes the forum accepts. */
const LANGUAGE_CODES = new Set(["en", "de", "fr", "es", "pt", "it"]);

export const LANGUAGE_COOKIE = "dsk_lang";

const ONE_YEAR_SECONDS = 31_536_000;

/** True for deskshare.com and its sub-domains, nothing else. */
function isDeskshareHost(hostname: string): boolean {
  const host = (hostname || "").toLowerCase();
  return host === "deskshare.com" || host.endsWith(".deskshare.com");
}

/**
 * Remembers a language the visitor picked, for the support forum.
 *
 * @returns the cookie string written, or "" when `lang` is not one of the six.
 */
export function rememberLanguageForForum(
  lang: string,
  where: Pick<Location, "hostname" | "protocol"> = window.location,
  doc: Pick<Document, "cookie"> = document,
): string {
  const code = (lang || "").toLowerCase();
  if (!LANGUAGE_CODES.has(code)) return "";
  const parts = [
    `${LANGUAGE_COOKIE}=${code}`,
    "Path=/",
    `Max-Age=${ONE_YEAR_SECONDS}`,
    "SameSite=Lax",
  ];
  if (isDeskshareHost(where.hostname)) parts.push("Domain=deskshare.com");
  if (where.protocol === "https:") parts.push("Secure");
  const cookie = parts.join("; ");
  try {
    doc.cookie = cookie;
  } catch {
    /* cookies blocked: the site's own language choice still works */
  }
  return cookie;
}
